import React, { useEffect, useState } from "react";
import { Add } from "@mui/icons-material";
import {
  getModels,
  getModelById,
  createModel,
  updateModel,
  deleteModel,
} from "../../../helper/apicalls/models";
import GlobalLoader from "../../common/GlobalLoader";
import CreateModelPopup from "../../common/CreateModelPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import ModelCard from "../ModelCard";
import CommonFilter from "../../common/commonFilter";
import "../../../styles/admin/models.scss";

const ModelsPage = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [popupMode, setPopupMode] = useState("create");
  const [selectedModel, setSelectedModel] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [filters, setFilters] = useState({});

  const fetchModels = async (type) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getModels(
        type
          ? {
              type,
            }
          : {}
      );
      setModels(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching models:", err);
      setError("Failed to load models");
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { label: "openai", value: "openai" },
    { label: "gemini", value: "gemini" },
    { label: "claude", value: "claude" },
    { label: "azure", value: "azure" },
  ];

  const handleSaveModel = async (payload) => {
    try {
      setLoading(true);
      const currentType = filters?.type || undefined;

      if (popupMode === "edit" && selectedModel?.id) {
        await updateModel(selectedModel.id, payload);
      } else {
        await createModel(payload);
      }
      await fetchModels(currentType);
      setShowCreatePopup(false);
      setSelectedModel(null);
    } catch (err) {
      console.error("Error saving model:", err);
      setError("Failed to save model");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setPopupMode("create");
    setSelectedModel(null);
    setShowCreatePopup(true);
  };

  const handleEditModel = async (model) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getModelById(model.id);
      const fullModel = response && response.data ? response.data : response;
      setPopupMode("edit");
      setSelectedModel(fullModel || model);
      setShowCreatePopup(true);
    } catch (err) {
      console.error("Error loading model for edit:", err);
      setError("Failed to load model details");
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (action, model) => {
    setSelectedModel(model);
    setConfirmAction(action);
    setIsConfirmOpen(true);
  };

  const handleDeleteRequest = (model) => {
    openConfirm("delete", model);
  };

  const handleToggleActiveRequest = (model) => {
    openConfirm("toggle", model);
  };

  const handleConfirm = async () => {
    if (!selectedModel?.id) {
      setIsConfirmOpen(false);
      setConfirmAction(null);
      return;
    }

    try {
      setLoading(true);
      if (confirmAction === "delete") {
        await deleteModel(selectedModel.id);
      } else if (confirmAction === "toggle") {
        await updateModel(selectedModel.id, {
          is_active: !selectedModel.is_active,
        });
      }
      const currentType = filters?.type || undefined;
      await fetchModels(currentType);
    } catch (err) {
      console.error("Error updating model:", err);
      setError(
        confirmAction === "delete"
          ? "Failed to delete model"
          : "Failed to update model status"
      );
    } finally {
      setLoading(false);
      setIsConfirmOpen(false);
      setSelectedModel(null);
      setConfirmAction(null);
    }
  };

  useEffect(() => {
    const currentType = filters?.type || undefined;
    fetchModels(currentType);
  }, [filters?.type]);

  return (
    <div className="models-page">
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="models-page__card">
        <div className="models-page__header">
          <span>Models</span>
          <div className="models-page__actions">
            {typeOptions.length > 0 && (
              <CommonFilter
                sections={[
                  {
                    key: "type",
                    title: "Type",
                    multiple: false,
                    options: typeOptions,
                  },
                ]}
                values={filters}
                onMultiChange={setFilters}
              />
            )}
            <button
              type="button"
              className="models-page__add-btn"
              onClick={handleOpenCreate}
            >
              <Add className="action-button-icon" />
              <span>Add Model</span>
            </button>
          </div>
        </div>
        <div className="models-page__grid">
          {!loading && !error && (
            <>
              {models?.map((model) => (
                <ModelCard
                  key={model.id || model.name}
                  model={model}
                  onEdit={handleEditModel}
                  onDelete={handleDeleteRequest}
                  onToggleActive={handleToggleActiveRequest}
                />
              ))}
            </>
          )}
        </div>
        {error && <div className="models-page__error">{error}</div>}
      </div>

      {showCreatePopup && (
        <CreateModelPopup
          open={showCreatePopup}
          initialModel={popupMode === "edit" ? selectedModel : null}
          handleClose={() => {
            setShowCreatePopup(false);
            setSelectedModel(null);
            setPopupMode("create");
          }}
          onSave={handleSaveModel}
        />
      )}

      {isConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmOpen}
          setIsConfirmDeleteOpen={setIsConfirmOpen}
          handleDelete={handleConfirm}
          title={
            confirmAction === "delete"
              ? "Delete model"
              : selectedModel?.is_active
              ? "Deactivate model"
              : "Activate model"
          }
          message={
            confirmAction === "delete"
              ? "Are you sure you want to delete this model? This action cannot be undone."
              : selectedModel?.is_active
              ? "Are you sure you want to deactivate this model?"
              : "Are you sure you want to activate this model?"
          }
          confirmLabel={
            confirmAction === "delete"
              ? "Delete"
              : selectedModel?.is_active
              ? "Deactivate"
              : "Activate"
          }
        />
      )}
    </div>
  );
};

export default ModelsPage;
